import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import LoadingPageLayout from 'common/layouts/loading-page';
import {
  selectIsLoggedIn,
  selectToken,
  selectUserEmailConfirmed,
} from 'redux/features/auth/authSlice';
import { isEmpty } from 'common/utils/isEmpty';

const RouteGuard = () => {
  // STATES
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // HOOKS
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const token = useSelector(selectToken);
  const isEmailConfirmed = useSelector(selectUserEmailConfirmed);
  // DATA INITIALIZATION
  const currentPath = location.pathname;

  // Updated authRoutes to include dynamic property signup paths
  const isAuthRoute = (path) => {
    const staticAuthRoutes = [
      '/login',
      '/signup', // Base signup
      '/confirm-account',
      '/reset-email',
      '/reset-password-request',
      '/reset-password-success',
      '/email-login-request', // Added this from your routing
      '/email-login-exchange', // Added this from your routing
      '/attach-plan', // Added this from your routing
    ];
    if (staticAuthRoutes.includes(path)) {
      return true;
    }
    if (path.startsWith('/signup/properties/')) {
      // Handle dynamic property signup
      return true;
    }
    // Add other dynamic auth route checks if needed
    // e.g., if (path.startsWith('/some-other-auth-path/')) return true;
    return false;
  };

  const confirmEmailRoute = ['/confirm-email'];
  const dashboardRoute = '/dashboard/home';
  const IsUserLoggedIn = isLoggedIn && !isEmpty(token);

  // HOOKS: Memoized Callback Function
  const authCheck = useCallback(() => {
    if (IsUserLoggedIn) {
      if (
        currentPath.includes(dashboardRoute) &&
        !isEmailConfirmed &&
        currentPath !== '/confirm-email'
      ) {
        // Ensure not to redirect from confirm-email itself
        setIsAuthenticated(false);
        navigate('/confirm-email', {
          state: { from: location },
          replace: true,
        });
      } else if (isAuthRoute(currentPath)) {
        // Use the new isAuthRoute function
        setIsAuthenticated(false);
        navigate(!isEmailConfirmed ? '/confirm-email' : '/dashboard/home', {
          replace: true,
        });
      } else if (confirmEmailRoute.includes(currentPath) && isEmailConfirmed) {
        setIsAuthenticated(false);
        navigate('/welcome', {
          replace: true,
        });
      } else {
        setIsAuthenticated(true);
      }
    } else if (!IsUserLoggedIn && !isAuthRoute(currentPath)) {
      // Use the new isAuthRoute function
      setIsAuthenticated(false);
      navigate('/login', {
        state: { from: location },
        replace: true,
      });
    } else {
      setIsAuthenticated(true);
    }
  }, [location, IsUserLoggedIn, isEmailConfirmed, currentPath, navigate]); // Added dependencies

  // SIDE EFFECTS
  useEffect(() => {
    // Call the AuthCheck function to run here.
    authCheck();
  }, [authCheck]); // authCheck is now stable due to useCallback and its dependencies

  // RENDER
  if (isAuthenticated) {
    return <Outlet />;
  } else {
    // It's important that this doesn't infinitely loop if a redirect target itself is guarded.
    // The LoadingPageLayout is fine here as the authCheck logic will perform the redirect.
    return <LoadingPageLayout />;
  }
};
export default RouteGuard;
